/**
 * Czysta logika Weterynarza „Pieskowo Weterynarz" — czynności pielęgnacyjne.
 * Darmowa troska (nie zakup); stan wizyty testowalny bez Phasera.
 */

export type VetCareId = 'stethoscope' | 'bandage' | 'thermometer';

export type VetCareAction = {
  id: VetCareId;
  namePl: string;
  /** Klucz tekstury proceduralnej (bez emoji-obiektów w scenie). */
  textureKey: string;
  /** Komunikat po wykonaniu czynności. */
  statusPl: string;
  /** Kolor kafla UI. */
  color: number;
};

export const VET_CARE_ACTIONS: readonly VetCareAction[] = [
  {
    id: 'stethoscope',
    namePl: 'Osłuchanie',
    textureKey: 'vet_tool_stethoscope',
    statusPl: 'Piesek zbadany!',
    color: 0xb3e5fc,
  },
  {
    id: 'bandage',
    namePl: 'Bandaż',
    textureKey: 'vet_tool_bandage',
    statusPl: 'Łapka zabandażowana!',
    color: 0xffccbc,
  },
  {
    id: 'thermometer',
    namePl: 'Temperatura',
    textureKey: 'vet_tool_thermometer',
    statusPl: 'Temperatura w normie!',
    color: 0xc8e6c9,
  },
] as const;

/** Komunikat po ukończeniu wszystkich czynności wizyty. */
export const VET_HEALTHY_MESSAGE_PL = 'Piesek zdrowy i szczęśliwy!' as const;

/** Dekoracyjne tekstury weterynarza (roślina, leki). */
export const VET_DECO_TEXTURES = {
  plant: 'vet_deco_plant',
  medicine: 'vet_deco_medicine',
} as const;

export type VetVisitState = {
  /** Ukończone czynności w tej wizycie. */
  completed: readonly VetCareId[];
};

export function emptyVisitState(): VetVisitState {
  return { completed: [] };
}

export function listCareActions(): readonly VetCareAction[] {
  return VET_CARE_ACTIONS;
}

export function careById(id: string): VetCareAction | undefined {
  return VET_CARE_ACTIONS.find((a) => a.id === id);
}

export function isCareDone(state: VetVisitState, id: VetCareId): boolean {
  return state.completed.includes(id);
}

export function allCaresDone(state: VetVisitState): boolean {
  return VET_CARE_ACTIONS.every((a) => state.completed.includes(a.id));
}

export function careProgress(state: VetVisitState): {
  done: number;
  total: number;
} {
  return {
    done: state.completed.length,
    total: VET_CARE_ACTIONS.length,
  };
}

/** Etykieta postępu, np. „Opieka: 1/3". */
export function progressLabelPl(state: VetVisitState): string {
  const { done, total } = careProgress(state);
  return `Opieka: ${done}/${total}`;
}

export type PerformCareResult = {
  state: VetVisitState;
  /** Czy czynność była już wcześniej zrobiona. */
  alreadyDone: boolean;
  /** Czy właśnie domknięto wszystkie czynności. */
  justCompletedAll: boolean;
  /** Status PL do HUD. */
  statusPl: string;
  action: VetCareAction | null;
};

/**
 * Wykonaj czynność pielęgnacyjną. Ponowne tapnięcie nie psuje stanu
 * (alreadyDone + ten sam komunikat zachęty).
 */
export function performCare(
  state: VetVisitState,
  careId: VetCareId,
): PerformCareResult {
  const action = careById(careId);
  if (!action) {
    return {
      state,
      alreadyDone: false,
      justCompletedAll: false,
      statusPl: 'Spróbuj inną czynność',
      action: null,
    };
  }

  if (isCareDone(state, careId)) {
    return {
      state,
      alreadyDone: true,
      justCompletedAll: false,
      statusPl: `${action.statusPl} (już zrobione)`,
      action,
    };
  }

  const next: VetVisitState = {
    completed: [...state.completed, careId],
  };
  const justCompletedAll = allCaresDone(next);
  return {
    state: next,
    alreadyDone: false,
    justCompletedAll,
    statusPl: justCompletedAll ? VET_HEALTHY_MESSAGE_PL : action.statusPl,
    action,
  };
}

/** Reset wizyty (np. ponowne wejście na scenę). */
export function resetVisit(_state?: VetVisitState): VetVisitState {
  return emptyVisitState();
}
